import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../../../hooks/useAuth";
import { useZoomSettings } from "../../../../../hooks/useZoomSettings";
import {
  createMissionQuantityIncreaseAPI,
  getMissionQuantityIncreaseAPI,
  updateMissionQuantityIncreaseAPI,
} from "../../../../../api/index";
import { isSpecialUser } from "../../../../../utils/environment";
import "../AdDetailModal.css";
import "./QuantityIncreaseModal.css";

const QuantityIncreaseModal = ({
  isOpen,
  onClose,
  adData,
  unitPrice, // 1회 단가
  onSave, // 저장 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const { settings: zoomSettings } = useZoomSettings();
  const endSubmitTime =
    zoomSettings?.end_submit != null &&
    String(zoomSettings.end_submit).match(/^\d{1,2}:\d{2}$/)
      ? zoomSettings.end_submit
      : "16:00";

  const [newDailyQuantity, setNewDailyQuantity] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputError, setInputError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false); // 확인 모달 표시 상태
  const [hasPendingRequest, setHasPendingRequest] = useState(false); // 해당 광고 PENDING 또는 APPROVED 수량신청 존재 여부
  const [hasApprovedRequest, setHasApprovedRequest] = useState(false); // 해당 광고 APPROVED(적용 대기) 건 존재 여부
  const [pendingRequestIds, setPendingRequestIds] = useState([]); // 해당 광고 PENDING/APPROVED 건 id 목록 (신청 시 CANCELLED 처리용)
  const [pendingCheckLoading, setPendingCheckLoading] = useState(false);

  // 현재 dailyQuantity 계산
  const getCurrentDailyQuantity = () => {
    let dailyQuantity = adData.dailyQuantity || 0;
    if (
      !dailyQuantity &&
      adData.totalQuantity &&
      adData.startDate &&
      adData.endDate
    ) {
      // totalQuantity와 광고 기간으로 역산
      const startDate = new Date(adData.startDate.split("T")[0]);
      const endDate = new Date(adData.endDate.split("T")[0]);
      const totalDays =
        Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
        ) + 1;
      if (totalDays > 0) {
        dailyQuantity = Math.round(adData.totalQuantity / totalDays);
      }
    }
    return dailyQuantity;
  };

  const currentDailyQuantity = getCurrentDailyQuantity();
  // REACT_APP_SPECIAL_USER_IDS에 없는 사용자는 일일 수량을 100의 배수로만 입력
  const requireMultipleOf100 = !isSpecialUser(user?.id);
  const minDailyQuantity = requireMultipleOf100
    ? Math.max(100, Math.ceil(currentDailyQuantity / 100) * 100)
    : Math.max(100, currentDailyQuantity);

  // 날짜 포맷팅 함수 (YYYY-MM-DD -> YYYY.MM.DD)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";
    return dateString.replace(/-/g, ".");
  };

  // 적용일부터 endDate까지의 일수 계산 (년월일만 사용)
  const calculateRemainingDays = () => {
    if (!adData.endDate) return 0;

    // 적용일 계산 (getApplicationDateUtc 로직과 동일)
    const match = String(endSubmitTime || "16:00").match(/^(\d{1,2}):(\d{2})$/);
    const cutoffHour = match ? parseInt(match[1], 10) : 16;
    const cutoffMinute = match ? parseInt(match[2], 10) : 0;

    const now = new Date();
    const koreaOffset = 9 * 60 * 60 * 1000;
    const dayMs = 24 * 60 * 60 * 1000;
    const koreaNow = new Date(now.getTime() + koreaOffset);
    const dayOfWeek = koreaNow.getUTCDay(); // 0=일, 1=월, ..., 6=토
    const hour = koreaNow.getUTCHours();
    const minute = koreaNow.getUTCMinutes();

    let daysToAdd;
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      daysToAdd = dayOfWeek === 0 ? 2 : 3;
    } else {
      const isAfterCutoff =
        hour > cutoffHour || (hour === cutoffHour && minute >= cutoffMinute);
      daysToAdd = isAfterCutoff ? 2 : 1;
    }

    const koreaMs = now.getTime() + koreaOffset;
    const koreaDayStartMs =
      (Math.floor(koreaMs / dayMs) + daysToAdd) * dayMs;
    const utcDayStartMs = koreaDayStartMs - koreaOffset;
    
    // 적용일을 한국시간으로 변환하여 년월일만 추출
    const appliedDate = new Date(utcDayStartMs + koreaOffset);
    const appliedYear = appliedDate.getUTCFullYear();
    const appliedMonth = appliedDate.getUTCMonth();
    const appliedDay = appliedDate.getUTCDate();
    const appliedDateOnly = new Date(appliedYear, appliedMonth, appliedDay);

    // endDate는 UTC로 저장되어 있으므로 한국시간으로 변환
    // UTC 2025-11-05T14:59:59.000Z → 한국시간 2025-11-05 23:59:59
    const utcEndDate = new Date(adData.endDate);
    const koreaEndDate = new Date(utcEndDate.getTime() + 9 * 60 * 60 * 1000); // UTC+9

    // 년월일만 추출
    const endYear = koreaEndDate.getFullYear();
    const endMonth = koreaEndDate.getMonth();
    const endDay = koreaEndDate.getDate();
    const endDateOnly = new Date(endYear, endMonth, endDay);

    // 적용일부터 종료일까지 일수 계산
    const diffTime = endDateOnly.getTime() - appliedDateOnly.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24); // 밀리초를 일수로 변환

    // 적용일부터 종료일까지 포함한 일수 (적용일 포함, 종료일 포함)
    // diffDays가 0이면 같은 날 = 1일
    // diffDays가 1이면 다음날까지 = 2일
    const remainingDays = Math.max(1, Math.floor(diffDays) + 1);

    return remainingDays;
  };

  const remainingDays = calculateRemainingDays();

  // 입력값을 숫자로 변환
  const parsedNewDailyQuantity = parseInt(newDailyQuantity) || 0;

  // 100의 배수 검사 (특별 사용자 제외)
  const isValidMultipleOf100 = !requireMultipleOf100 || parsedNewDailyQuantity % 100 === 0;

  // 추가 수량 계산
  const calculateAdditionalQuantity = () => {
    if (parsedNewDailyQuantity < minDailyQuantity) return 0;
    if (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) return 0;
    const additionalDailyQuantity =
      parsedNewDailyQuantity - currentDailyQuantity;
    return additionalDailyQuantity * remainingDays;
  };

  const additionalQuantity = calculateAdditionalQuantity();

  // 추가 금액 계산
  const calculateAdditionalAmount = () => {
    return additionalQuantity * unitPrice;
  };

  const additionalAmount = calculateAdditionalAmount();

  // 모달이 열릴 때 초기값 설정
  useEffect(() => {
    if (isOpen) {
      setNewDailyQuantity(minDailyQuantity.toString());
      setInputError("");
    }
  }, [isOpen, minDailyQuantity]);

  // 모달 열릴 때 해당 광고로 PENDING 또는 APPROVED 수량신청이 있는지 확인
  useEffect(() => {
    if (!isOpen || (!adData?.ad_id && !adData?.id) || !user?.id) {
      setHasPendingRequest(false);
      setHasApprovedRequest(false);
      setPendingRequestIds([]);
      return;
    }
    const adId = adData.ad_id || adData.id;
    let cancelled = false;
    setPendingCheckLoading(true);
    setHasPendingRequest(false);
    setHasApprovedRequest(false);
    setPendingRequestIds([]);

    const matchAd = (row) =>
      String(row.ad_id) === String(adId) || row.ad_id === adId;

    Promise.all([
      getMissionQuantityIncreaseAPI({
        webMemberId: user.id,
        status: "PENDING",
        page: 1,
        pageSize: 500,
      }),
      getMissionQuantityIncreaseAPI({
        webMemberId: user.id,
        status: "APPROVED",
        page: 1,
        pageSize: 500,
      }),
    ])
      .then(([pendingRes, approvedRes]) => {
        if (cancelled) return;
        const pendingList = Array.isArray(pendingRes?.data?.data)
          ? pendingRes.data.data
          : [];
        const approvedList = Array.isArray(approvedRes?.data?.data)
          ? approvedRes.data.data
          : [];
        const forThisAd = [
          ...pendingList.filter(matchAd),
          ...approvedList.filter(matchAd),
        ];
        const ids = forThisAd
          .map((row) => row.id)
          .filter((id) => id != null);
        const hasApproved = forThisAd.some((row) => row.status === "APPROVED");
        setPendingRequestIds(ids);
        setHasPendingRequest(ids.length > 0);
        setHasApprovedRequest(hasApproved);
      })
      .catch(() => {
        if (!cancelled) {
          setHasPendingRequest(false);
          setHasApprovedRequest(false);
          setPendingRequestIds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPendingCheckLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, adData?.ad_id, adData?.id, user?.id]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, isProcessing, onClose]);

  // 버튼 disabled 상태 디버깅
  useEffect(() => {
    if (isOpen) {
      const isDisabled =
        isProcessing ||
        parsedNewDailyQuantity < minDailyQuantity ||
        parsedNewDailyQuantity === currentDailyQuantity ||
        (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) ||
        additionalAmount > (user?.deposit || 0) ||
        inputError !== "";

      console.log("버튼 disabled 상태:", {
        isDisabled,
        isProcessing,
        parsedNewDailyQuantity,
        minDailyQuantity,
        currentDailyQuantity,
        additionalAmount,
        userDeposit: user?.deposit,
        inputError,
      });
    }
  }, [
    isOpen,
    isProcessing,
    parsedNewDailyQuantity,
    minDailyQuantity,
    currentDailyQuantity,
    additionalAmount,
    user?.deposit,
    inputError,
  ]);

  // 입력값 검증
  const validateInput = (value) => {
    if (value === "") {
      setInputError("");
      return true;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < minDailyQuantity) {
      setInputError(
        `일일 수량은 최소 ${minDailyQuantity}회 이상이어야 합니다.`
      );
      return false;
    }

    if (requireMultipleOf100 && numValue % 100 !== 0) {
      setInputError("일일 수량은 100의 배수로 입력해 주세요.");
      return false;
    }

    setInputError("");
    return true;
  };

  // 입력 핸들러
  const handleInputChange = (e) => {
    const value = e.target.value;

    // 빈 값 또는 숫자만 허용
    if (value === "" || /^\d+$/.test(value)) {
      setNewDailyQuantity(value);
      validateInput(value);
    }
  };

  // 포커스 아웃 시 검증
  const handleInputBlur = () => {
    if (newDailyQuantity === "") {
      setNewDailyQuantity(minDailyQuantity.toString());
      setInputError("");
      return;
    }
    if (parsedNewDailyQuantity < minDailyQuantity) {
      setNewDailyQuantity(minDailyQuantity.toString());
      setInputError("");
      return;
    }
    if (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) {
      const rounded = Math.round(parsedNewDailyQuantity / 100) * 100;
      const clamped = Math.max(minDailyQuantity, rounded);
      setNewDailyQuantity(clamped.toString());
      setInputError("");
    }
  };

  // 확인 버튼 클릭 시 확인 모달 표시
  const handleConfirmClick = () => {
    console.log("확인 버튼 클릭됨 - 검증 시작");

    if (parsedNewDailyQuantity < minDailyQuantity) {
      alert(
        `일일 수량은 최소 ${minDailyQuantity} 이상이어야 합니다. (현재: ${currentDailyQuantity})`
      );
      return;
    }

    if (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) {
      alert("일일 수량은 100의 배수로 입력해 주세요.");
      return;
    }

    if (parsedNewDailyQuantity === currentDailyQuantity) {
      alert("변경된 수량이 없습니다.");
      return;
    }

    if (additionalAmount > (user?.deposit || 0)) {
      alert("예치금이 부족합니다.");
      return;
    }

    // 커스텀 확인 모달 표시
    console.log("확인 모달 표시");
    setShowConfirmModal(true);
  };

  // 확인 모달에서 확인 버튼 클릭 시 실제 처리
  const handleConfirmSubmit = async () => {
    console.log("확인 모달에서 확인 클릭 - 처리 시작");
    setShowConfirmModal(false);
    await processIncrease();
  };

  // 확인 모달에서 취소 버튼 클릭 시
  const handleConfirmCancel = () => {
    console.log("확인 모달에서 취소 클릭");
    setShowConfirmModal(false);
  };

  // 적용일 계산: 평일 end_submit 전/후 → 다음날·다다음날, 주말 요청 → 월요일 확인 후 화요일 적용
  const getApplicationDateUtc = () => {
    const match = String(endSubmitTime || "16:00").match(/^(\d{1,2}):(\d{2})$/);
    const cutoffHour = match ? parseInt(match[1], 10) : 16;
    const cutoffMinute = match ? parseInt(match[2], 10) : 0;

    const now = new Date();
    const koreaOffset = 9 * 60 * 60 * 1000;
    const dayMs = 24 * 60 * 60 * 1000;
    const koreaNow = new Date(now.getTime() + koreaOffset);
    const dayOfWeek = koreaNow.getUTCDay(); // 0=일, 1=월, ..., 6=토
    const hour = koreaNow.getUTCHours();
    const minute = koreaNow.getUTCMinutes();

    let daysToAdd;
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      daysToAdd = dayOfWeek === 0 ? 2 : 3;
    } else {
      const isAfterCutoff =
        hour > cutoffHour || (hour === cutoffHour && minute >= cutoffMinute);
      daysToAdd = isAfterCutoff ? 2 : 1;
    }

    const koreaMs = now.getTime() + koreaOffset;
    const koreaDayStartMs =
      (Math.floor(koreaMs / dayMs) + daysToAdd) * dayMs;
    const utcDayStartMs = koreaDayStartMs - koreaOffset;
    const startDateUTC = new Date(utcDayStartMs).toISOString();

    const applyDate = new Date(utcDayStartMs + koreaOffset);
    const y = applyDate.getUTCFullYear();
    const m = applyDate.getUTCMonth();
    const d = applyDate.getUTCDate();
    const applicationDateLabel = `${y}.${String(m + 1).padStart(2, "0")}.${String(d).padStart(2, "0")}`;

    return { startDateUTC, appliedDateUTC: startDateUTC, applicationDateLabel };
  };

  // 실제 수량 증가 요청 생성 (기존 PENDING 건은 CANCELLED 후 새 PENDING 생성)
  const processIncrease = async () => {
    try {
      setIsProcessing(true);

      // 해당 광고로 기존 PENDING 건이 있으면 모두 CANCELLED로 변경
      if (pendingRequestIds.length > 0) {
        await Promise.all(
          pendingRequestIds.map((id) =>
            updateMissionQuantityIncreaseAPI({ id, status: "CANCELLED" })
          )
        );
      }

      const currentTotalQuantity = adData.totalQuantity || 0;
      const currentPrice = adData.price || 0;
      const newTotalQuantity = currentTotalQuantity + additionalQuantity;
      const newPrice = currentPrice + additionalAmount;

      const { startDateUTC, appliedDateUTC } = getApplicationDateUtc();

      const missionTemplate =
        adData.missionTemplate ?? adData.mission_template_id;
      const additionalDailyQuantity =
        parsedNewDailyQuantity - currentDailyQuantity;
      const newTodayRemainingCount =
        missionTemplate === 5 || missionTemplate === 6
          ? newTotalQuantity
          : (adData.todayRemainingCount ?? currentDailyQuantity) +
            additionalDailyQuantity;

      const updatedAdData = {
        ...adData,
        totalQuantity: newTotalQuantity,
        dailyQuantity: parsedNewDailyQuantity,
        price: newPrice,
        todayRemainingCount: newTodayRemainingCount,
      };

      const payload = {
        ad_id: adData.ad_id || adData.id,
        webMemberId: adData.webMemberId ?? user?.id,
        username: adData.username || user?.username || "",
        name: adData.name || user?.name || user?.username || adData.username || "",
        missionName: adData.missionName || adData.adName || "광고",
        missionType: adData.missionType ?? 1,
        missionTemplate: adData.missionTemplate ?? adData.mission_template_id ?? 1,
        status: "PENDING",
        startDate: startDateUTC,
        endDate: adData.endDate,
        appliedDate: appliedDateUTC,
        price: currentPrice,
        newPrice,
        beforeTotalQuantity: currentTotalQuantity,
        newTotalQuantity,
        beforeDailyQuantity: currentDailyQuantity,
        newDailyQuantity: parsedNewDailyQuantity,
        adminMemo: "수량 증가 요청",
        showMemo: "",
        data: updatedAdData,
        adminId: user?.id ?? 1,
      };

      await createMissionQuantityIncreaseAPI(payload);

      alert("수량 증가 요청이 등록되었습니다. 승인 후 반영됩니다.");
      onClose();
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("수량 증가 요청 실패:", error);
      alert(
        `수량 증가 요청에 실패했습니다: ${
          error.response?.data?.msg || error.message || "알 수 없는 오류"
        }`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="quantity-increase-modal-overlay"
      onClick={(e) => {
        // 오버레이 클릭 시 모달 닫기 (처리 중이 아닐 때만)
        if (e.target === e.currentTarget && !isProcessing) {
          onClose();
        }
      }}
    >
      <div
        className="quantity-increase-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="quantity-increase-modal-header">
          <h2>수량 증가</h2>
          <button
            className="quantity-increase-modal-close-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="quantity-increase-modal-content">
          {/* 승인 대기 중인 신청이 있을 때 안내 */}
          {pendingCheckLoading ? (
            <div className="quantity-increase-pending-check">
              확인 중...
            </div>
          ) : hasPendingRequest ? (
            <div className="quantity-increase-pending-notice">
              {hasApprovedRequest
                ? "적용 대기 중인 상품이 있습니다. 신청을 하면 기존 건이 취소되고 새로 신청이 됩니다."
                : "승인 대기 중인 상품이 있습니다. 신청을 하면 기존 건이 취소되고 새로 신청이 됩니다."}
            </div>
          ) : null}

          {/* 적용일 안내 */}
          {(() => {
            const { applicationDateLabel } = getApplicationDateUtc();
            return (
              <div className="quantity-increase-notice">
                <div className="quantity-increase-notice-title">적용일 안내</div>
                <ul className="quantity-increase-notice-list">
                  <li>
                    평일 {endSubmitTime} 전 요청: 승인 후 <strong>그 다음날</strong> 적용
                  </li>
                  <li>
                    평일 {endSubmitTime} 이후 요청: 승인 후 <strong>그 다다음날</strong> 적용
                  </li>
                  <li>주말 요청: <strong>월요일 확인 후 화요일</strong>부터 적용</li>
                </ul>
                <div className="quantity-increase-notice-apply">
                  이 요청 기준 예상 적용일: <strong>{applicationDateLabel}</strong>
                </div>
              </div>
            );
          })()}

          {/* 예치금 정보 */}
          <div className="quantity-increase-deposit-info">
            <span className="quantity-increase-deposit-label">예치금:</span>
            <span className="quantity-increase-deposit-amount">
              {(user?.deposit || 0).toLocaleString()} 원
            </span>
          </div>

          {/* 광고 정보 */}
          <div className="quantity-increase-ad-info">
            <div className="quantity-increase-info-row">
              <label>광고명</label>
              <div>{adData.missionName || "-"}</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>현재 일일 수량</label>
              <div>{currentDailyQuantity.toLocaleString()}회</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>광고 종료일</label>
              <div>
                {adData.endDate
                  ? formatDateForDisplay(adData.endDate.split("T")[0])
                  : "-"}
              </div>
            </div>
            <div className="quantity-increase-info-row">
              <label>잔여 일수</label>
              <div>{remainingDays}일</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>1회 단가</label>
              <div>{unitPrice.toLocaleString()}원</div>
            </div>
          </div>

          {/* 수량 입력 */}
          <div className="quantity-increase-input-section">
            <label htmlFor="newDailyQuantity">
              새로운 일일 수량 (최소: {minDailyQuantity}회
              {requireMultipleOf100 ? ", 100의 배수" : ""})
            </label>
            <input
              id="newDailyQuantity"
              type="text"
              inputMode="numeric"
              value={newDailyQuantity}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={
                requireMultipleOf100
                  ? `최소 ${minDailyQuantity}회 이상, 100 단위로 입력`
                  : `최소 ${minDailyQuantity}회 이상 입력`
              }
              className={`quantity-increase-input ${
                inputError ? "quantity-increase-input-error" : ""
              }`}
            />
            {inputError && (
              <div className="quantity-increase-error">{inputError}</div>
            )}
          </div>

          {/* 계산 결과 */}
          {parsedNewDailyQuantity >= minDailyQuantity &&
            isValidMultipleOf100 &&
            parsedNewDailyQuantity > currentDailyQuantity && (
              <div className="quantity-increase-result">
                <div className="quantity-increase-result-row">
                  <label>추가 일일 수량</label>
                  <div>
                    {(
                      parsedNewDailyQuantity - currentDailyQuantity
                    ).toLocaleString()}
                    회
                  </div>
                </div>
                <div className="quantity-increase-result-row">
                  <label>총 추가 수량</label>
                  <div>{additionalQuantity.toLocaleString()}회</div>
                </div>
                <div className="quantity-increase-result-row highlight">
                  <label>추가 금액</label>
                  <div>{additionalAmount.toLocaleString()}원</div>
                </div>
              </div>
            )}
        </div>

        {/* 푸터 */}
        <div className="quantity-increase-modal-footer">
          <button
            className="quantity-increase-cancel-btn"
            onClick={onClose}
            disabled={isProcessing}
          >
            취소
          </button>
          <button
            className="quantity-increase-confirm-btn"
            onClick={(e) => {
              console.log("확인 버튼 클릭됨", e);
              e.preventDefault();
              e.stopPropagation();
              handleConfirmClick();
            }}
            disabled={
              isProcessing ||
              parsedNewDailyQuantity < minDailyQuantity ||
              parsedNewDailyQuantity === currentDailyQuantity ||
              (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) ||
              additionalAmount > (user?.deposit || 0) ||
              inputError !== ""
            }
            style={{
              cursor:
                isProcessing ||
                parsedNewDailyQuantity < minDailyQuantity ||
                parsedNewDailyQuantity === currentDailyQuantity ||
                (requireMultipleOf100 && parsedNewDailyQuantity % 100 !== 0) ||
                additionalAmount > (user?.deposit || 0) ||
                inputError !== ""
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {isProcessing ? "처리 중..." : "확인"}
          </button>
        </div>
      </div>

      {/* 확인 모달 */}
      {showConfirmModal && (
        <div
          className="quantity-increase-confirm-modal-overlay"
          onClick={handleConfirmCancel}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="quantity-increase-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  margin: "0 0 16px 0",
                  color: "#1f2937",
                }}
              >
                수량 증가 확인
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "#374151",
                  lineHeight: "1.6",
                  margin: "0 0 8px 0",
                }}
              >
                일일 수량을 <strong>{currentDailyQuantity.toLocaleString()}회</strong>에서{" "}
                <strong>{parsedNewDailyQuantity.toLocaleString()}회</strong>로
                증가시키겠습니까?
              </p>
              <div
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>
                    추가 일일 수량:
                  </span>
                  <span style={{ fontWeight: "600", fontSize: "14px" }}>
                    {(
                      parsedNewDailyQuantity - currentDailyQuantity
                    ).toLocaleString()}
                    회
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>
                    총 추가 수량:
                  </span>
                  <span style={{ fontWeight: "600", fontSize: "14px" }}>
                    {additionalQuantity.toLocaleString()}회
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "8px",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <span style={{ color: "#374151", fontSize: "15px", fontWeight: "600" }}>
                    추가 금액:
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "16px",
                      color: "#2563eb",
                    }}
                  >
                    {additionalAmount.toLocaleString()}원
                  </span>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={handleConfirmCancel}
                style={{
                  padding: "12px 24px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#374151",
                  fontSize: "15px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f9fafb";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "white";
                }}
              >
                취소
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={isProcessing}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "8px",
                  background: isProcessing ? "#9ca3af" : "#10b981",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: "500",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  if (!isProcessing) {
                    e.target.style.background = "#059669";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isProcessing) {
                    e.target.style.background = "#10b981";
                  }
                }}
              >
                {isProcessing ? "처리 중..." : "확인"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default QuantityIncreaseModal;
